import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Chip, EmptyState } from "@/components/ui-bits";
import { Upload, Search, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Editors() {
    const [editors, setEditors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [house, setHouse] = useState("");
    const [facets, setFacets] = useState({ houses: [], categories: [], total: 0 });
    const [importOpen, setImportOpen] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (cat) params.set("category", cat);
            if (house) params.set("house", house);
            const [rows, fac] = await Promise.all([
                api.get("/editors?" + params.toString()).then((r) => r.data),
                api.get("/editors/facets").then((r) => r.data),
            ]);
            setEditors(rows);
            setFacets(fac);
        } catch (e) {
            toast.error("Failed to load editors");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cat, house]);

    useEffect(() => {
        const t = setTimeout(fetchAll, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const commonCats = useMemo(
        () => ["PB", "BB", "ER", "Chapter books", "MG", "YA", "Graphic Novel", "Nonfiction", "Fiction"],
        []
    );

    // Long-tail categories in the "All (from data)" optgroup — computed by
    // filter over facets.categories on every keystroke; memoize so a 500+
    // editor list doesn't recompute on every unrelated re-render.
    const extraCategories = useMemo(
        () => (facets.categories || []).filter((c) => !commonCats.includes(c)),
        [facets.categories, commonCats],
    );

    return (
        <div>
            <PageHeader
                eyebrow="Directory"
                title="Editors"
                description={`${facets.total} editors on file. Filter by category, publisher, or search names & imprints.`}
                testId="editors-header"
                right={
                    <button
                        className="btn-primary"
                        data-testid="editors-import-open"
                        onClick={() => setImportOpen(true)}
                    >
                        <Upload className="w-4 h-4" strokeWidth={1.5} /> Import CSV
                    </button>
                }
            />

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 mb-6" data-testid="editors-filters">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                    <input
                        className="input-flat pl-10"
                        placeholder="Search name, imprint, house, notes…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="editors-search"
                    />
                </div>
                <select
                    className="input-flat"
                    value={cat}
                    onChange={(e) => setCat(e.target.value)}
                    data-testid="editors-filter-category"
                >
                    <option value="">All categories</option>
                    {commonCats.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                    <optgroup label="All (from data)">
                        {extraCategories.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </optgroup>
                </select>
                <select
                    className="input-flat min-w-[220px]"
                    value={house}
                    onChange={(e) => setHouse(e.target.value)}
                    data-testid="editors-filter-house"
                >
                    <option value="">All publishers</option>
                    {facets.houses.map((h) => (
                        <option key={h} value={h}>{h}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="border border-border rounded-sm overflow-hidden bg-white/70" data-testid="editors-table">
                <div className="grid grid-cols-[1.6fr_1fr_1.6fr_1fr_2fr_40px] px-4 py-3 label-mono border-b border-border bg-secondary/60">
                    <div>Name / Title</div>
                    <div>House</div>
                    <div>Imprint</div>
                    <div>Categories</div>
                    <div>Email</div>
                    <div />
                </div>
                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground pulse-mono font-mono">
                        Loading editors…
                    </div>
                ) : editors.length === 0 ? (
                    <div className="p-10 text-center">
                        <EmptyState
                            title="No editors found"
                            hint="Try changing filters or import a CSV to get started."
                            testId="editors-empty"
                        />
                    </div>
                ) : (
                    <div>
                        {editors.map((e) => (
                            <div
                                key={e.id}
                                data-testid={`editor-row-${e.id}`}
                                className="grid grid-cols-[1.6fr_1fr_1.6fr_1fr_2fr_40px] px-4 py-3 border-b border-border/60 items-center hover:bg-secondary/40 transition-colors text-sm"
                            >
                                <div>
                                    <div className="font-medium">{e.name}</div>
                                    <div className="label-mono mt-0.5">{e.title || "—"}</div>
                                </div>
                                <div className="truncate">{e.house || "—"}</div>
                                <div className="truncate">{e.imprint || "—"}</div>
                                <div className="flex flex-wrap gap-1">
                                    {(e.categories || []).slice(0, 3).map((c) => (
                                        <Chip key={c}>{c}</Chip>
                                    ))}
                                    {e.categories?.length > 3 && (
                                        <span className="label-mono">+{e.categories.length - 3}</span>
                                    )}
                                </div>
                                <div className="truncate font-mono text-xs">{e.email || "—"}</div>
                                <button
                                    className="text-muted-foreground hover:text-[#8C2D19] transition-colors"
                                    data-testid={`editor-delete-${e.id}`}
                                    onClick={async () => {
                                        if (!confirm(`Remove ${e.name}?`)) return;
                                        await api.delete(`/editors/${e.id}`);
                                        toast.success("Editor removed");
                                        fetchAll();
                                    }}
                                    title="Remove"
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {importOpen && (
                <ImportDialog
                    onClose={() => setImportOpen(false)}
                    onDone={() => {
                        setImportOpen(false);
                        fetchAll();
                    }}
                />
            )}
        </div>
    );
}

function ImportDialog({ onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [mode, setMode] = useState("add_new");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!file) return toast.error("Choose a CSV file");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post(`/editors/import?mode=${mode}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(res.data);
            const r = res.data;
            toast.success(
                `Imported. ${r.inserted} new · ${r.updated} updated · ${r.skipped || 0} skipped`
            );
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="import-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-lg p-8 bg-background">
                <div className="label-mono mb-3">Import</div>
                <h3 className="font-serif text-3xl mb-2">Import editors from CSV</h3>
                <p className="text-sm text-muted-foreground mb-6">
                    Expected columns: Name, House, Imprint, Title, Email, Preferences sub, Notes, MSWL Link,
                    MSWL One Sheet, Preferred Pronouns, Status.
                </p>

                <label className="block mb-4">
                    <span className="label-mono">CSV file</span>
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="mt-2 block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-primary file:text-primary-foreground file:rounded-sm hover:file:bg-[#12241F]"
                        data-testid="import-file-input"
                    />
                </label>

                <div className="mb-6">
                    <div className="label-mono mb-2">Mode</div>
                    <div className="flex gap-2 flex-wrap">
                        {[
                            ["add_new", "Add new only"],
                            ["upsert", "Upsert by email"],
                            ["append", "Append"],
                            ["replace", "Replace all"],
                        ].map(([k, v]) => (
                            <button
                                key={k}
                                onClick={() => setMode(k)}
                                data-testid={`import-mode-${k}`}
                                className={
                                    "px-3 py-1.5 text-sm rounded-sm border transition-colors " +
                                    (mode === k
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "border-border hover:bg-secondary")
                                }
                            >
                                {v}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                        {mode === "add_new" && "Only rows whose email doesn't already exist will be inserted. Existing editors are left untouched."}
                        {mode === "upsert" && "Match on email — update existing editors, insert new ones."}
                        {mode === "append" && "Blindly add every row (may create duplicates)."}
                        {mode === "replace" && "Danger: wipes the entire editor database, then loads the CSV."}
                    </p>
                </div>

                <div className="flex gap-2 justify-end">
                    <button className="btn-ghost" onClick={onClose} data-testid="import-cancel">
                        Cancel
                    </button>
                    <button
                        className="btn-primary disabled:opacity-60"
                        onClick={submit}
                        disabled={busy || !file}
                        data-testid="import-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Importing…" : "Import"}
                    </button>
                </div>
            </div>
        </div>
    );
}
